/* eslint-disable no-unsafe-optional-chaining */
import React, { useCallback } from 'react'
import { Alert, H2, Separator } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { FormTextField, useZionFormContext, useFormState } from '@fs/zion-form'
import { expandedNameFieldsSortMap, useSlsHighlightsContext } from '@fs/zion-sls'

/**
 * Custom Validation function for the step 4.
 * @param {Object} values - The values from the form.
 * @param {Function} t - The translation function.
 * @returns {Object} - The Yup schema for the fourth step.
 */

export const validateStep4 = (values, t) => {
  const errors = { step4: {} }
  let atLeastOneFilled = false
  const step4Fields = values?.step4

  if (step4Fields) {
    Object.keys(step4Fields).forEach((key) => {
      const field = values.step4[key]
      const text = field.text.trim()

      const isValid = /^[a-zA-Z0-9À-ž\s\-',.]+$/.test(text)
      if (!isValid) {
        if (text) {
          errors.step4[key] = {
            text: {
              message: t(
                'task-viewer.error.illegal-characters',
                "You can only enter ' - , . diacritic, and alphanumeric characters."
              ),
            },
          }
        }
      } else {
        atLeastOneFilled = true
      }
    })

    if (!atLeastOneFilled) {
      errors.step4.root = {
        message: t('full-name-correction.step4.must-have-name-value', 'Given Name or Surname must not be empty.'),
      }
    }
  }

  if (Object.keys(errors?.step4).length === 0) {
    delete errors.step4
  }

  return { values, errors }
}

/**
 * The ReviewStep4 component.
 * @param {Object} props - The component props.
 * @param {Object} props.types - The SlsTypes object .
 * @returns {JSX.Element} - The JSX element representing the ReviewStep4 component.
 */
const ReviewStep4 = ({ types = [] }) => {
  const [t] = useTranslation()
  const h = useSlsHighlightsContext()
  const { getValues, control } = useZionFormContext()
  const { errors } = useFormState({ control })

  const step4Values = getValues('step4')
  const nameKeys = Object.keys(step4Values)
  const sortedNameKeys = nameKeys.sort(
    (a, b) =>
      expandedNameFieldsSortMap.find((sort) => sort.fieldType === a)?.value -
      expandedNameFieldsSortMap.find((sort) => sort.fieldType === b)?.value
  )

  const getLabel = useCallback(
    (nameKey) => types?.fields?.find((type) => type.fieldType === nameKey)?.displayLabel,
    [types?.fields]
  )

  return (
    <>
      <H2 size="H5">{t('full-name-correction.step4.heading', 'Correct the spelling of each part of this name.')}</H2>
      <Separator size="xs" />
      {sortedNameKeys.map((nameKey) => (
        <FormTextField
          data-testid={nameKey}
          key={nameKey}
          name={`step4.${nameKey}.text`}
          label={getLabel(nameKey)}
          placeholder={getLabel(nameKey)}
          onFocus={() => {
            h.hoverHighlights({ elementIds: step4Values[nameKey].highlightIds })
          }}
          onBlur={() => {
            h.hoverHighlights({ elementIds: [] })
          }}
        />
      ))}
      {errors?.step4?.root && (
        <>
          <Separator size="xs" />
          <Alert type="error">{errors.step4.root.message}</Alert>
        </>
      )}
    </>
  )
}

export default ReviewStep4
